import Strategy from './Strategy.js'
import { con, sfu } from '../messages'
import { jsonRpc } from '../JsonRpc'
import { localStream } from 'stream/src/index.js'
import { Device, types } from 'mediasoup-client'
import { mediator, messenger } from '../index.js'
import { getMediasoupHandlerName } from '../../../browser/src/index.js'

interface ProducerAppData extends types.AppData {
  source: string
  trackId: string
}

type Producer = types.Producer<ProducerAppData>
type ProducerMap = Map<string, Producer>
type ConsumerMap = Map<string, types.Consumer>
type DataConsumerMap = Map<string, types.DataConsumer>
type TransportResponse = {
  id: string,
  iceParameters: types.IceParameters
  iceCandidates: types.IceCandidate[]
  dtlsParameters: types.DtlsParameters
  sctpParameters: types.SctpParameters
}
type ConsumeResponse = {
  id: string
  kind: types.MediaKind
  rtpParameters: types.RtpParameters
}
type ConsumeDataResponse = {
  id: string
  sctpStreamParameters: types.SctpStreamParameters
  label: string
  protocol: string
}
class SFUStrategy extends Strategy {
  name: string
  peerId: string | null
  isReady: boolean
  private device!: types.Device

  private sendTransport!: Promise<types.Transport>
  private sendTransportResolve!: (transport: types.Transport) => void
  private recvTransport!: Promise<types.Transport>
  private recvTransportResolve!: (transport: types.Transport) => void

  private producers: ProducerMap
  private consumers: ConsumerMap
  private dataConsumers: DataConsumerMap
  private dataProducer?: types.DataProducer
  private transports: types.Transport[] = [] // send and recv transport of the current initialize()

  constructor() {
    super()
    this.name = 'sfu'
    this.producers = new Map()
    this.consumers = new Map()
    this.dataConsumers = new Map()
    this.peerId = null
    this.isReady = false
    jsonRpc.on('sfu:consume', this.handleConsume)
    jsonRpc.on('sfu:consumeData', this.handleConsumeData)
    jsonRpc.on('sfu:recordingStarted', this.handleRecordingStarted)
    jsonRpc.on('sfu:recordingStopped', this.handleRecordingStopped)
    jsonRpc.on('sfu:closeProducer', this.handleCloseProducer)
    jsonRpc.on('sfu:closeDataProducer', this.handleCloseDataProducer)
  }

  async initialize() {
    this.producers.forEach(producer => {
      producer.close()
    })
    this.producers.clear()
    this.consumers.forEach(consumer => {
      consumer.close()
    })
    this.consumers.clear()
    this.dataConsumers.forEach(dataConsumer => {
      dataConsumer.close()
    })
    this.dataConsumers.clear()
    this.peerId = null
    this.isReady = false
    this.transports = []
    this.sendTransport = new Promise(resolve => {
      this.sendTransportResolve = resolve
    })
    this.recvTransport = new Promise(resolve => {
      this.recvTransportResolve = resolve
    })
    const { rtpCapabilities } = await jsonRpc.sendAndWait<{ rtpCapabilities: types.RtpCapabilities }>(sfu.getRouterRtpCapabilitiesMessage())
    if (rtpCapabilities.headerExtensions) {
      rtpCapabilities.headerExtensions = rtpCapabilities.headerExtensions.filter(
        ext => ext.uri !== 'urn:3gpp:video-orientation'
      )
    }
    if (!this.device) {
      this.device = await Device.factory({ handlerName: await getMediasoupHandlerName() })
    }
    if (!this.device.loaded) {
      try {
        await this.device.load({ routerRtpCapabilities: rtpCapabilities })
      } catch (error) {
        messenger.emit('log:dumpTrigger', { reason: 'device-load-error', error })
        console.info('Error loading device with router RTP capabilities', { error })
        throw error
      }
    }
    await this.createTransports()
    messenger.subscribe(this.name, 'strategy:muted', this.handleMuted)
    messenger.subscribe(this.name, 'strategy:unmuted', this.handleUnmuted)
  }

  handleConsume = async (event: { detail: any }) => {
    const data = event.detail
    console.info(`sfu:consume <= ${JSON.stringify(data)}`)
    const transport = await this.recvTransport
    const { producerId, source } = data
    // Verify if consumer for this producerId already exists
    if (this.consumers.has(producerId)) {
      console.info('Consumer already exists for producerId:', producerId)
      return
    }
    const { id, kind, rtpParameters } = await jsonRpc.sendAndWait<ConsumeResponse>(
      sfu.consumeMessage(producerId, transport.id, this.device!.rtpCapabilities)
    )
    const consumer = await transport.consume({
      id,
      producerId,
      kind,
      rtpParameters,
    })
    this.consumers.set(consumer.producerId, consumer)
    consumer.observer.on('close', () => {
      this.consumers.delete(consumer.producerId)
    })
    jsonRpc.send(sfu.resumeConsumerMessage(transport.id, consumer.id))
    const stream = new MediaStream([consumer.track])
    if (source === 'screen') {
      this.emit('strategy:screensharingStarted:viewer', { stream })
    } else {
      this.emit(`strategy:stream:${kind as 'video' | 'audio'}`, { stream })
    }
  }

  handleConsumeData = async (event: { detail: any }) => {
    const data = event.detail
    console.info(`sfu:consumeData <= ${JSON.stringify(data)}`)
    const transport = await this.recvTransport
    const { dataProducerId } = data
    if (!dataProducerId) {
      return
    }
    // verify if data consumer for this dataProducerId already exists
    if (this.dataConsumers.has(dataProducerId)) {
      console.info('Data consumer already exists for dataProducerId:', dataProducerId)
      return
    }
    const { id, sctpStreamParameters, label, protocol } = await jsonRpc.sendAndWait<ConsumeDataResponse>(
      sfu.consumeDataMessage(dataProducerId, transport.id)
    )

    const dataConsumer = await transport.consumeData({
      id,
      dataProducerId,
      sctpStreamParameters,
      label,
      protocol,
    })
    dataConsumer.observer.on('close', () => {
      this.dataConsumers.delete(dataConsumer.dataProducerId)
    })
    dataConsumer.on('message', (message: string) => {
      let parsed: any
      try {
        parsed = JSON.parse(message)
      } catch {
        parsed = message
      }
      this.emit('strategy:data', parsed)
    })
    this.dataConsumers.set(dataConsumer.dataProducerId, dataConsumer)
  }

  broadcast(data: any): void {
    if (this.dataProducer && this.dataProducer.readyState === 'open') {
      const message = typeof data === 'string' ? data : JSON.stringify(data)
      this.dataProducer.send(message)
    }
  }

  async replaceTrack(type: string, _oldTrack: MediaStreamTrack, newTrack: MediaStreamTrack, stream: MediaStream): Promise<void> {
    const transport = await this.sendTransport
    for (const producer of this.producers.values()) {
      if (producer.appData.source !== 'screen' && producer.kind === type) {
        await producer.replaceTrack({ track: newTrack })
        console.info(`${type} track replaced`)
        jsonRpc.send(sfu.syncRecordingMessage(transport.id, producer.id))
      }
    }
  }

  async destroy() {
    super.destroy()
    try {
      for (const producer of this.producers.values()) {
        producer.close()
      }
      this.producers.clear()
      for (const consumer of this.consumers.values()) {
        consumer.close()
      }
      this.consumers.clear()
      const sendTransport = await this.sendTransport
      if (sendTransport) {
        sendTransport.close()
      }
      const recvTransport = await this.recvTransport
      if (recvTransport) {
        recvTransport.close()
      }
      this.dataProducer?.close()
      for (const dataConsumer of this.dataConsumers.values()) {
        dataConsumer.close()
      }
      this.dataConsumers.clear()
      if (this.peerId) {
        await new Promise(resolve => {
          jsonRpc.send(
            sfu.leaveRoomMessage({
              peerId: this.peerId,
            }),
            () => {
              resolve(null)
            }
          )
        })
      }
      console.info('SFU strategy destroyed')
    } catch (error) {
      console.info('Error destroying SFU strategy', { error })
    }
    messenger.unsubscribe('strategy:muted')
    messenger.unsubscribe('strategy:unmuted')
  }

  isStrategyReady(): boolean {
    // A transport whose connection is lost stays so: no server method restarts its ICE (transport.restartIce needs new
    // iceParameters from the server), only a websocket reconnect or a reload initializes the strategy again
    return (
      this.isReady &&
      this.transports.every(({ connectionState }) => connectionState !== 'failed' && connectionState !== 'disconnected')
    )
  }

  handleError = (error: any): void => {
    console.info(`SFU strategy error: ${error}`, { error })
    console.error('SFU Strategy Error:', error)
    this.emit('strategy:error', { error })
  }

  async getTotalBits(): Promise<number> {
    let totalBits = 0
    const peers = [...this.producers.values(), ...this.consumers.values()]
    for (const peer of peers) {
      const stats = await peer.getStats()
      for (const report of stats.values()) {
        if (report.type === 'transport') {
          totalBits += report.bytesSent || 0
          totalBits += report.bytesReceived || 0
        }
      }
    }
    return totalBits
  }

  async startRecording() {
    await this.sendTransport
    await this.recvTransport
    jsonRpc.send(sfu.startRecordingMessage())
  }

  stopRecording() {
    jsonRpc.send(sfu.stopRecordingMessage())
  }

  handleRecordingStarted = () => {
    console.info('sfu:recordingStarted')
    this.emit('strategy:recording:started', void 0)
  }

  handleRecordingStopped = () => {
    console.info('sfu:recordingStopped')
    this.emit('strategy:recording:stopped', void 0)
  }

  shareScreen = async (stream: MediaStream) => {
    await this.produceTrack(stream.getVideoTracks()[0], 'video', 'screen')
  }

  destroyScreensharing = () => {
    for (const producer of this.producers.values()) {
      if (producer.appData.source === 'screen') {
        producer.close()
        this.producers.delete(producer.id)
        this.closeProducer(producer.id)
        break
      }
    }
  }

  private async createTransports() {
    const { id, iceParameters, iceCandidates, dtlsParameters, sctpParameters } = await jsonRpc.sendAndWait<TransportResponse>(sfu.createTransportMessage())
    await this.createLocalSendTransport(id, iceParameters, iceCandidates, dtlsParameters, sctpParameters)
    const {
      id: recvId,
      iceParameters: recvIceParameters,
      iceCandidates: recvIceCandidates,
      dtlsParameters: recvDtlsParameters,
      sctpParameters: recvSctpParameters,
    } = await jsonRpc.sendAndWait<TransportResponse>(sfu.createTransportMessage())
    await this.createLocalRecvTransport(
      recvId,
      recvIceParameters,
      recvIceCandidates,
      recvDtlsParameters,
      recvSctpParameters
    )
    await this.start()
  }

  private async createLocalSendTransport(
    id: string,
    iceParameters: types.IceParameters,
    iceCandidates: types.IceCandidate[],
    dtlsParameters: types.DtlsParameters,
    sctpParameters?: types.SctpParameters
  ) {
    const transport = this.device.createSendTransport({
      id,
      iceParameters,
      iceCandidates,
      dtlsParameters,
      sctpParameters,
      iceServers: mediator.session?.configuration.iceServers || [],
    })
    transport.on('connect', async ({ dtlsParameters }, callback, errorBack) => {
      try {
        await jsonRpc.sendAndWait(sfu.connectTransportMessage(transport!.id, dtlsParameters))
        callback()
      } catch (error: any) {
        messenger.emit('log:dumpTrigger', { reason: 'send-transport-connect-error', error: error })
        errorBack(error)
      }
    })
    transport.on('produce', async (data, callback, errorBack) => {
      const { kind, rtpParameters, appData } = data
      const { source, trackId } = appData as ProducerAppData
      try {
        const { id } = await jsonRpc.sendAndWait<{ id: string }>(
          sfu.produceMessage(transport!.id, kind, rtpParameters, source, trackId)
        )
        callback({ id })
      } catch (error: any) {
        messenger.emit('log:dumpTrigger', { reason: 'produce-error', error: error })
        errorBack(error)
      }
    })
    transport.on('producedata', async (data, callback, errorBack) => {
      const { sctpStreamParameters, label, protocol, appData } = data
      try {
        const { id } = await jsonRpc.sendAndWait<{ id: string }>(
          sfu.produceDataMessage(transport!.id, sctpStreamParameters, appData, protocol, label)
        )
        callback({ id })
      } catch (error: any) {
        messenger.emit('log:dumpTrigger', { reason: 'produce-data-error', error: error })
        errorBack(error)
      }
    })
    transport.on('connectionstatechange', async connectionState => {
      if (connectionState === 'failed' || connectionState === 'disconnected') {
        console.info(`WebRTC connection state is: ${connectionState}`)
        messenger.emit('log:dumpTrigger', { reason: 'recv-transport-connection-failed', error: { connectionState } })
        // Check built-in diagnostics for ICE/DTLS failures
        // Navigate to chrome://webrtc-internals in your browser
      }
    })
    this.transports.push(transport)
    this.sendTransportResolve(transport)
    console.info(`Send transport ${transport.id} created`)
  }

  private async createLocalRecvTransport(
    id: string,
    iceParameters: types.IceParameters,
    iceCandidates: types.IceCandidate[],
    dtlsParameters: types.DtlsParameters,
    sctpParameters?: types.SctpParameters
  ) {
    const transport = this.device.createRecvTransport({
      id,
      iceParameters,
      iceCandidates,
      dtlsParameters,
      sctpParameters,
      iceServers: mediator.session?.configuration.iceServers || [],
    })
    transport.on('connect', async ({ dtlsParameters }, callback, errorBack) => {
      try {
        await jsonRpc.sendAndWait(sfu.connectTransportMessage(transport!.id, dtlsParameters))
        callback()
      } catch (error: any) {
        messenger.emit('log:dumpTrigger', { reason: 'recv-transport-connect-error', error: error })
        errorBack(error)
      }
    })
    transport.on('connectionstatechange', async connectionState => {
      if (connectionState === 'failed' || connectionState === 'disconnected') {
        console.info(`WebRTC connection state is: ${connectionState}`)
        messenger.emit('log:dumpTrigger', { reason: 'recv-transport-connection-failed', error: { connectionState } })
        // Check built-in diagnostics for ICE/DTLS failures
        // Navigate to chrome://webrtc-internals in your browser
      }
    })
    this.transports.push(transport)
    this.recvTransportResolve(transport)
    console.info(`Recv transport ${transport.id} created`)
  }

  private async start() {
    await this.startProducing()
    await this.createDataProducer()
    jsonRpc.send(con.readyMessage(), (error: any, response: any) => {
      if (!error) {
        this.isReady = true
        this.emit('strategy:ready', void 0)
      } else {
        console.info(`endpoint: con:ready couldn\'t been sent`, {
          response,
        })
      }
    })
  }

  private async startProducing() {
    const { video, audio } = localStream
    if (video.stream.value && video.stream.value.getVideoTracks().length > 0) {
      await this.produceTrack(video.stream.value.getVideoTracks()[0], 'video', 'camera')
    }
    if (audio.stream.value && audio.stream.value.getAudioTracks().length > 0) {
      await this.produceTrack(audio.stream.value.getAudioTracks()[0], 'audio', 'microphone')
    }
  }

  private async produceTrack(track: MediaStreamTrack, kind: string, source: string): Promise<types.Producer> {
    const transport = await this.sendTransport
    try {
      const producer = await transport.produce<ProducerAppData>({
        track,
        stopTracks: false,
        appData: {
          source,
          trackId: track.id,
        },
      })
      this.producers.set(producer.id, producer)
      producer.observer.on('close', () => {
        this.producers.delete(kind)
        this.closeProducer(producer.id)
      })
      return producer
    } catch (error) {
      console.info(`Failed to produce ${kind}`, { error })
      throw error
    }
  }

  private async closeProducer(producerId: string): Promise<void> {
    const transport = await this.sendTransport
    await jsonRpc.sendAndWait(sfu.closeProducerMessage(transport.id, producerId))
  }

  private async createDataProducer(label = 'data', protocol = '') {
    const transport = await this.sendTransport
    try {
      this.dataProducer = await transport.produceData({
        ordered: true,
        label,
        protocol,
      })
    } catch (error) {
      console.info(`Failed to produce data ${error}`)
      throw error
    }
  }
  getMeasureStats = () => {
    const peers = [...this.producers.values(), ...this.consumers.values()]
    for (const peer of peers) {
      if (peer.kind === 'video') {
        return peer.getStats()
      }
    }
    return null
  }
  handleCloseProducer = ({ detail: { producerId } }: { detail: { producerId: string } }) => {
    console.info(`sfu:closeProducer <= ${JSON.stringify({ producerId })}`)
    this.consumers.get(producerId)?.close()
    this.consumers.delete(producerId)
  }
  handleCloseDataProducer = ({ detail: { dataProducerId } }: { detail: { dataProducerId: string } }) => {
    console.info(`sfu:closeDataProducer <= ${JSON.stringify({ dataProducerId })}`)
    this.dataConsumers.forEach(dataConsumer => {
      if (dataConsumer.dataProducerId === dataProducerId) {
        dataConsumer.close()
        this.dataConsumers.delete(dataConsumer.id)
      }
    })
  }
  handleMuted = ({ type }: { type: string }) => {
    this.producers.forEach(producer => {
      if (producer.appData.source !== 'screen' && producer.kind === type) {
        producer.pause()
      }
    })
  }
  handleUnmuted = ({ type }: { type: string }) => {
    this.producers.forEach(producer => {
      if (producer.appData.source !== 'screen' && producer.kind === type) {
        producer.resume()
      }
    })
  }
}

const sfuStrategy = new SFUStrategy()
export { sfuStrategy }
